import { useRef, useCallback, useState } from 'react';
import { CameraControls } from '@react-three/drei';
import { useAdminSubjectDraft, LabelDraft } from '@/contexts';

const generateId = () => Math.random().toString(36).slice(2);

const defaultLabel = (): LabelDraft => ({
  _id: generateId(),
  title: '',
  slug: '',
  description: '',
  primaryCategoryId: null,
  categoryIds: [],
  unitIds: [],
  config3D: {
    position: [0, 0, 0],
    camera: {
      rotation: { azimuthAngle: 0, polarAngle: Math.PI / 2 },
      position: [0, 0, 100],
      focalOffset: [0, 0, 0],
      distance: 100,
    },
  },
});

/**
 * All label list state and interactions for Step 5.
 *
 * Extracts placement, camera capture, CRUD, and accordion logic out of the
 * step component so it contains only JSX.
 */
export function useLabelManager() {
  const { draft, updateDraft, updateConfig } = useAdminSubjectDraft();
  const [placingLabelId, setPlacingLabelId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeLabelId, setActiveLabelId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [forceVisible, setForceVisible] = useState(false);
  const cameraControlsRef = useRef<CameraControls | null>(null);
  const labelRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const onCameraControlsReady = useCallback((controls: CameraControls | null) => {
    cameraControlsRef.current = controls;
  }, []);

  const scrollToLabel = (id: string) => {
    setTimeout(() => {
      labelRowRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const flyToLabel = (label: LabelDraft) => {
    const controls = cameraControlsRef.current;
    if (!controls) return;
    const { camera } = label.config3D;
    controls.setPosition(camera.position[0], camera.position[1], camera.position[2], true);
    controls.dollyTo(camera.distance, true);
    controls.setFocalOffset(camera.focalOffset[0], camera.focalOffset[1], camera.focalOffset[2], true);
    controls.rotateTo(camera.rotation.azimuthAngle, camera.rotation.polarAngle, true);
    controls.normalizeRotations();
  };

  const updateLabel = (id: string, patch: Partial<LabelDraft>) => {
    updateDraft({ labels: draft.labels.map((l) => (l._id === id ? { ...l, ...patch } : l)) });
  };

  const addLabel = () => {
    const label = defaultLabel();
    updateDraft({ labels: [...draft.labels, label] });
    setExpandedId(label._id);
    setActiveLabelId(label._id);
    setPlacingLabelId(label._id);
    scrollToLabel(label._id);
  };

  const removeLabel = (id: string) => {
    updateDraft({ labels: draft.labels.filter((l) => l._id !== id) });
    if (placingLabelId === id) setPlacingLabelId(null);
    if (expandedId === id) setExpandedId(null);
  };

  const toggleExpanded = (id: string) => {
    const opening = expandedId !== id;
    setExpandedId(opening ? id : null);
    setActiveLabelId(opening ? id : null);
    if (opening) {
      const label = draft.labels.find((l) => l._id === id);
      if (label) flyToLabel(label);
      scrollToLabel(id);
    }
  };

  const handleLabelClick = (labelId: string) => {
    if (activeLabelId === labelId) {
      setActiveLabelId(null);
      return;
    }
    setPlacingLabelId(null);
    setActiveLabelId(labelId);
    setExpandedId(labelId);
    const label = draft.labels.find((l) => l._id === labelId);
    if (label) flyToLabel(label);
    scrollToLabel(labelId);
  };

  const handleMeshClick = (position: [number, number, number]) => {
    if (!placingLabelId) return;
    updateLabel(placingLabelId, {
      config3D: {
        ...draft.labels.find((l) => l._id === placingLabelId)!.config3D,
        position,
      },
    });
    setPlacingLabelId(null);
  };

  const captureCamera = (labelId: string) => {
    const controls = cameraControlsRef.current;
    if (!controls) return;
    const pos = controls.getPosition({ x: 0, y: 0, z: 0 } as any);
    const focalOffset = controls.getFocalOffset({ x: 0, y: 0, z: 0 } as any);
    updateLabel(labelId, {
      config3D: {
        ...draft.labels.find((l) => l._id === labelId)!.config3D,
        camera: {
          rotation: { azimuthAngle: controls.azimuthAngle, polarAngle: controls.polarAngle },
          position: [pos.x ?? 0, pos.y ?? 0, pos.z ?? 0],
          focalOffset: [focalOffset.x ?? 0, focalOffset.y ?? 0, focalOffset.z ?? 0],
          distance: controls.distance,
        },
      },
    });
  };

  return {
    draft,
    updateConfig,
    placingLabelId,
    setPlacingLabelId,
    expandedId,
    activeLabelId,
    pendingDeleteId,
    setPendingDeleteId,
    forceVisible,
    setForceVisible,
    labelRowRefs,
    onCameraControlsReady,
    addLabel,
    removeLabel,
    toggleExpanded,
    updateLabel,
    handleLabelClick,
    handleMeshClick,
    captureCamera,
  };
}
